import React, { MutableRefObject } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { Typography, Chip } from 'Components/atoms';
import { RiDeleteBin6Line } from '@react-icons/all-files/ri/RiDeleteBin6Line';
import { ImPencil } from '@react-icons/all-files/im/ImPencil';
import {
    RULES_COL_RULE_ID,
    RULES_COL_CONNECTOR,
    RULES_COL_RULE_SET,
    RULES_COL_PRIORITY,
    RULES_COL_CONDITIONS,
    RULES_COL_ACTIONS,
    RULES_COL_CREATED,
    RULES_EDIT_RULE_TITLE,
    RULES_DELETE_RULE_TITLE,
    RULES_NO_CONDITIONS,
} from 'Core-utils/constants/constants';
import { ConditionGroupPayload, RuleCondition, RuleDto } from 'Core-utils/types/rules.types';

// Flatten all conditions from a conditionGroups tree into a single array
function flattenGroups(groups: ConditionGroupPayload[]): RuleCondition[] {
    const out: RuleCondition[] = [];
    for (const g of groups) {
        for (const c of g.conditions ?? []) {
            out.push(c as RuleCondition);
        }
        out.push(...flattenGroups(g.childGroups ?? []));
    }
    return out;
}

// Returns all conditions regardless of whether the rule uses the legacy flat
// format or the newer conditionGroups tree.
function getConditions(row: RuleDto): RuleCondition[] {
    if ((row.conditions ?? []).length > 0) {
        return row.conditions!;
    }
    return flattenGroups(row.conditionGroups ?? []);
}

// ─── ActionBadge — small inline component, scoped to this grid ───────────────
const ActionBadge = ({ type, children }: { type: 'then' | 'else'; children: React.ReactNode }) => (
    <span className={`action-badge action-badge--${type}`}>{children}</span>
);

type T = (key: string, fallback?: string) => string;

export function getRulesGridColumns(
    t: T,
    // Refs so cell renderers always read the latest values without requiring useMemo re-creation
    connectorMapRef: MutableRefObject<Record<string, string>>,
    ruleSetMapRef: MutableRefObject<Record<string, string>>,
    onEditClickRef: MutableRefObject<(row: RuleDto) => void>,
    onDeleteClickRef: MutableRefObject<(id: string) => void>,
    onViewConditions: (rule: RuleDto | null) => void,
): ColDef<RuleDto>[] {
    return [
        {
            colId: 'expander',
            headerName: '',
            cellRenderer: 'agGroupCellRenderer',
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            sortable: false,
            resizable: false,
            suppressHeaderMenuButton: true,
            cellStyle: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
            },
        },
        {
            colId: 'id',
            field: 'id',
            headerName: t(RULES_COL_RULE_ID, 'RULE ID'),
            sortable: true,
            width: 208,
            minWidth: 120,
            cellRenderer: (params: ICellRendererParams<RuleDto>) => (
                <Typography
                    variant="body5"
                    className="text-primary-500"
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                >
                    {params.value}
                </Typography>
            ),
        },
        {
            colId: 'connector',
            headerName: t(RULES_COL_CONNECTOR, 'CONNECTOR'),
            sortable: true,
            width: 192,
            minWidth: 100,
            // valueGetter reads from ref so sorting always reflects the latest map
            valueGetter: (p) => connectorMapRef.current[p.data?.cid ?? ''] ?? p.data?.cid ?? '',
            cellRenderer: (params: ICellRendererParams<RuleDto>) => (
                <Typography variant="body5" color="text-high-emphasis">
                    {params.value}
                </Typography>
            ),
        },
        {
            colId: 'rule_set',
            headerName: t(RULES_COL_RULE_SET, 'RULE SET'),
            sortable: true,
            width: 200,
            minWidth: 120,
            valueGetter: (p) => {
                const rsId = p.data?.rule_set_id;
                return rsId ? ruleSetMapRef.current[rsId] ?? rsId : '';
            },
            cellRenderer: (params: ICellRendererParams<RuleDto>) => {
                const rsId = params.data?.rule_set_id;
                if (!rsId) {
                    return (
                        <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                            —
                        </Typography>
                    );
                }
                const name = ruleSetMapRef.current[rsId];
                return (
                    <div className="d-flex flex-column gap-1">
                        <Typography variant="body5" color="text-high-emphasis">
                            {name ?? rsId}
                        </Typography>
                        {name && (
                            <Typography
                                variant="caption1"
                                color="text-low-emphasis"
                                style={{ fontFamily: 'monospace', fontSize: '11px' }}
                            >
                                {rsId}
                            </Typography>
                        )}
                    </div>
                );
            },
        },
        {
            colId: 'priority',
            field: 'priority',
            headerName: t(RULES_COL_PRIORITY, 'PRIORITY'),
            sortable: true,
            width: 128,
            minWidth: 80,
            cellRenderer: (params: ICellRendererParams<RuleDto>) => (
                <Chip value={String(params.value)} variant="status" />
            ),
        },
        {
            colId: 'conditions',
            headerName: t(RULES_COL_CONDITIONS, 'CONDITIONS'),
            sortable: true,
            flex: 1,
            minWidth: 200,
            autoHeight: true,
            wrapText: true,
            valueGetter: (p) => {
                const conds = p.data ? getConditions(p.data) : [];
                return conds[0]?.name ?? '';
            },
            cellRenderer: (params: ICellRendererParams<RuleDto>) => {
                const row = params.data;
                const allConds = row ? getConditions(row) : [];
                const cond = allConds[0];
                if (!cond) {
                    return (
                        <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                            {t(RULES_NO_CONDITIONS, 'None')}
                        </Typography>
                    );
                }
                const valStr =
                    typeof cond.value === 'object'
                        ? JSON.stringify(cond.value)
                        : String(cond.value ?? '');
                return (
                    <div className="d-flex flex-column gap-1 py-2 w-100 mw-0" style={{ minWidth: 0 }}>
                        <div
                            className="rules-condition-cell"
                            title={`${cond.name} ${cond.operator} ${valStr}`}
                        >
                            <span className="rules-condition-cell__type">{cond.type}</span>
                            <span className="rules-condition-cell__separator">·</span>
                            <span className="rules-condition-cell__name">{cond.name}</span>
                            <span className="rules-condition-cell__operator">{cond.operator}</span>
                            <span className="rules-condition-cell__value">{valStr}</span>
                        </div>
                        {allConds.length > 1 && (
                            <div
                                className="view-conditions-link cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onViewConditions(row ?? null);
                                }}
                            >
                                <Typography variant="caption1" color="primary-500">
                                    + {allConds.length - 1} more condition
                                    {allConds.length > 2 ? 's' : ''}
                                </Typography>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            colId: 'actions',
            headerName: t(RULES_COL_ACTIONS, 'ACTIONS'),
            sortable: true,
            width: 224,
            minWidth: 224,
            maxWidth: 224,
            resizable: false,
            autoHeight: true,
            wrapText: true,
            valueGetter: (p) => p.data?.actions?.[0]?.action_type ?? '',
            cellRenderer: (params: ICellRendererParams<RuleDto>) => {
                const acts = params.data?.actions ?? [];
                if (!acts.length) {
                    return (
                        <Typography variant="body5" color="text-low-emphasis" className="fst-italic">
                            —
                        </Typography>
                    );
                }
                return (
                    <div className="d-flex flex-column gap-1 py-1 w-100" style={{ minWidth: 0 }}>
                        {acts.map((a, i) => (
                            <div key={i} className="d-flex align-items-center gap-2">
                                <ActionBadge type={a.action_type}>
                                    {a.action_type.toUpperCase()}
                                </ActionBadge>
                                <span className="rules-action-arrow">→</span>
                                <code className="rules-action-code">
                                    {a.action_data?.type ?? 'UNKNOWN'}
                                </code>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            colId: 'createdAt',
            field: 'createdAt',
            headerName: t(RULES_COL_CREATED, 'CREATED'),
            sortable: true,
            width: 160,
            minWidth: 110,
            cellRenderer: (params: ICellRendererParams<RuleDto>) => {
                if (!params.value) {
                    return <Typography variant="body5" color="text-low-emphasis">—</Typography>;
                }
                const d = new Date(params.value);
                return (
                    <div className="d-flex flex-column">
                        <Typography variant="body5" color="text-high-emphasis">
                            {d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis">
                            {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                    </div>
                );
            },
        },
        {
            colId: 'row-actions',
            headerName: '',
            sortable: false,
            width: 96,
            minWidth: 72,
            suppressHeaderMenuButton: true,
            cellRenderer: (params: ICellRendererParams<RuleDto>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                return (
                    <div className="d-flex align-items-center gap-3">
                        <ImPencil
                            size={16}
                            className="rules-icon-btn"
                            title={t(RULES_EDIT_RULE_TITLE, 'Edit rule')}
                            onClick={() => onEditClickRef.current(row)}
                        />
                        <RiDeleteBin6Line
                            size={16}
                            className="rules-icon-btn rules-icon-btn--danger"
                            title={t(RULES_DELETE_RULE_TITLE, 'Delete rule')}
                            onClick={() => onDeleteClickRef.current(row.id)}
                        />
                    </div>
                );
            },
        },
    ];
}
